
import React from 'react';
import { User } from '@/types';
import { ChevronLeft, ChevronRight, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import AdminLinks from './AdminLinks';
import AuthorLinks from './AuthorLinks';

interface SidebarProps {
  user: User;
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, isOpen, onToggle }) => {
  const isAdmin = user.role === 'admin';
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-all duration-300 ease-in-out",
        isOpen 
          ? "w-72" 
          : "w-[70px]",
        "md:relative md:translate-x-0",
        !isOpen && "-translate-x-full md:translate-x-0"
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center gap-2 animate-fadeIn">
          <div className="rounded-full bg-royalty-600 p-1">
            <Wallet className="h-6 w-6 text-white" />
          </div>
          <span className={cn(
            "text-xl font-semibold text-royalty-700 dark:text-royalty-300 transition-opacity duration-300",
            !isOpen && "lg:hidden opacity-0 md:opacity-100"
          )}>RoyaltyTracker</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="md:flex"
        >
          {isOpen ? (
            <ChevronLeft className="h-6 w-6" />
          ) : (
            <ChevronRight className="h-6 w-6" />
          )}
          <span className="sr-only">Toggle sidebar</span>
        </Button>
      </div>
      <nav className="flex-1 overflow-y-auto p-2 md:p-4">
        <div className="space-y-1">
          {isAdmin ? (
            <AdminLinks isOpen={isOpen} />
          ) : (
            <AuthorLinks isOpen={isOpen} />
          )}
        </div>
      </nav>
      <div className="mt-auto border-t border-gray-200 dark:border-gray-800 p-4">
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "w-full group",
            !isOpen && "lg:p-2"
          )}
          onClick={onToggle}
        >
          <ChevronLeft className={cn(
            "h-4 w-4 transition-transform group-hover:scale-125", 
            !isOpen && "rotate-180"
          )} />
          <span className={cn(
            "ml-2",
            !isOpen && "lg:hidden"
          )}>
            {isOpen ? "Collapse" : "Expand"}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
